import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cl from 'classnames';
import { Company, HeadsetInvoice, PaymentStatusEnum } from 'src/models';
import { HEADSETS } from 'src/constants';
import {
  currencySign,
  formatDatetime,
  formatPaymentInitiator,
  formatPaymentStatus,
} from 'src/helper';
import { usePage, useContextData, useLanguage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import { useCompanyById } from 'src/services/company';
import {
  useHeadsetById,
  fetchHeadsetPaymentsAction,
} from 'src/services/headsets';
import {
  PageSwitcher,
  StickyTable,
  useStickyCols,
  StickyTableConfig,
} from 'src/components';
import { FilterModal, FilterModalButton } from 'src/components/general';
import styles from './headsetBillingStyle.module.scss';

const statusColor = (status: PaymentStatusEnum) => {
  switch (status) {
    case PaymentStatusEnum.succeeded:
      return 'green';
    case PaymentStatusEnum.pending:
      return 'warning';
    case PaymentStatusEnum.canceled:
      return 'pink';
  }
};

export const HeadsetBilling = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const {
    access: { accessMap },
  } = usePage();
  const {
    header: { width: headerWidth },
  } = useContextData();
  const dispatch = useAppDispatch();
  const { t, td } = useLanguage();
  const company = useCompanyById(companyId) as Company;
  const headset = useHeadsetById(headsetId);
  const [isLoading, setIsLoading] = useState<null | boolean>(null);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const invoices = useMemo(() => headset?.invoices || [], [headset?.invoices]);
  const headsetIsLoaded = useMemo(() => Boolean(headset), [headset]);

  useEffect(() => {
    if (!headsetIsLoaded) {
      return;
    }

    setIsLoading(true);

    dispatch(fetchHeadsetPaymentsAction({ companyId, headsetId })).then(() => {
      setIsLoading(false);
    });
  }, [dispatch, headsetIsLoaded, headsetId, companyId]);

  const config: StickyTableConfig<HeadsetInvoice>[] = useMemo(
    () => [
      {
        title: t('page.headset.billing.price'),
        key: 'price',
        field: 'price',
        render: (value, invoice) => (
          <div className={styles.price}>
            <div>{`${Number(value || 0).toFixed(0)} ${currencySign(company?.location?.currency || 'rub')}`}</div>
            {invoice.isTest && (
              <div className={cl(styles.tag, styles.blue)}>
                {t('general.test')}
              </div>
            )}
          </div>
        ),
      },
      {
        title: t('page.headset.billing.paymentStatus'),
        key: 'paymentStatus',
        field: 'paymentStatus',
        render: (value) => (
          <div
            className={cl(
              styles.tag,
              styles.tag120,
              styles[statusColor(value)],
            )}
          >
            {td(formatPaymentStatus(value))}
          </div>
        ),
      },
      {
        title: t('page.headset.billing.paymentInitiator'),
        key: 'paymentInitiator',
        field: 'paymentInitiator',
        render: (value) => td(formatPaymentInitiator(value)),
      },
      {
        title: t('page.headset.billing.createdAt'),
        key: 'createdAt',
        field: 'createdAt',
        render: (value) => (
          <div className={cl(styles.tag, styles.tag120, styles.gray)}>
            {formatDatetime(value)}
          </div>
        ),
      },
      {
        title: t('page.headset.billing.updatedAt'),
        key: 'updatedAt',
        field: 'updatedAt',
        render: (value) => (
          <div className={cl(styles.tag, styles.tag120, styles.gray)}>
            {formatDatetime(value)}
          </div>
        ),
      },
    ],
    [company?.location?.currency, t, td],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
        }}
      >
        {modalStickyColsFilter}
      </FilterModal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton setShow={setShowFiltersModal} />
          </div>
        </div>
        <div className={styles.filters}>{stickyColsFilter}</div>
      </div>
      <StickyTable
        config={config}
        data={invoices}
        withPaginator
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
      />
    </div>
  );
};

export default HeadsetBilling;
